
import { Vue, Options } from "vue-class-component";
import moment from "moment";
import { downloadFile } from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { DispatchController } from "@/app/ui/controllers/DispatchController";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { Dispatch } from "@/domain/entities/Dispatch";
import ellipsisString from "@/app/infrastructures/misc/common-library/EllipsisString";
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import { limitLengthName } from "@/app/ui/modules/receipt-v2/module";
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import {
  INTERNAL_SERVER_ERROR,
  CLIENT_ERROR,
  INTERNET_ERROR,
} from "@/app/infrastructures/misc/Utils";
import { onPrintDispatchManifest } from "./modules/manifest";
import { Endpoints } from "@/app/infrastructures/misc/Endpoints";

@Options({
  components: {
    Skeleton,
    EmptyList,
  },
})
export default class DispatchList extends Vue {
  isLoading = false;
  searchValue = "";
  detailManifest: Dispatch = new Dispatch();
  errorData = new ErrorMessageEntities();
  isError = false;

  get buttonActionError() {
    return {
      textColor: "white",
      title: this.$t("dispatch.reload"),
      iconLeft: "ics_o_reload",
    };
  }

  // table
  get columns() {
    return [
      {
        name: this.$t("dispatch.no"),
        hide: true,
        styleHead: "w-12 text-gray-lp-300 text-sm height-header-table",
        render: (_item: any, index: any) => {
          return `<div class="font-medium text-center text-gray-lp-300 text-sm">${index +
            1}</div>`;
        },
      },
      {
        name: this.$t("dispatch.jenisPengiriman"),
        styleHead: "w-24 text-gray-lp-800 text-sm font-normal",
        render: (item: any) => {
          return `<div class="flex flex-row gap-2">
            <div class="flex justify-start flex-col items-start text-sm">
              <div>
                ${item.ddSttNo}
              </div>
              <div class="font-medium rounded-xl bg-gray-lp-200 flex justify-center items-center text-gray-lp-300 px-2 mt-1">
                ${item.ddProductType || "-"}
              </div>
            </div>
            <div class="content-center">
              ${
                item.ddIsCod
                  ? item.ddIsDfod
                    ? `<img src="${require("@/app/ui/assets/svg/new-dfod.svg")}" class="h-2.5" />`
                    : `<img src="${require("@/app/ui/assets/svg/new-cod.svg")}" class="h-2.5" />`
                  : ""
              }
            </div>
          </div>`;
        },
      },
      {
        name: this.$t("dispatch.noReferensi"),
        hide: true,
        styleHead: "w-32 text-gray-lp-300 text-sm",
        render: (item: any) => {
          return `<div class="text-gray-lp-300 whitespace-no-wrap break-all break-words text-sm">${item.ddRefNo ||
            "-"}</div>`;
        },
      },
      {
        name: this.$t("dispatch.koli"),
        styleHead: "w-16 text-gray-lp-800 text-sm font-normal",
        render: (item: any) => {
          return `<div class="flex flex-col gap-0.5">
              <div class="text-gray-lp-300 text-sm">
              ${this.formatGrossWeight(item.ddGrossWeight)}
              </div>
              <div class="text-gray-lp-800 text-xs">
              ${
                item.ddPieces
                  ? `${item.ddPieces} ${this.$t("dispatch.koli")}`
                  : "-"
              }
              </div>
            </div>`;
        },
      },
      {
        name: this.$t("dispatch.ursaCode"),
        hide: true,
        styleHead: "w-16 text-gray-lp-300 text-sm",
        render: (item: any) => {
          return `<div class="text-gray-lp-300 text-sm">${item.ddDestinationUrsaCode ||
            "-"}</div>`;
        },
      },
      {
        name: this.$t("dispatch.noDem"),
        hide: true,
        styleHead: "w-24 text-gray-lp-300 text-sm",
        render: (item: any) => {
          return `<div class="text-gray-lp-300 text-sm break-words">${item.ddDemNo ||
            "-"}</div>`;
        },
      },
    ];
  }

  get firstRowHeaderColumns() {
    return [
      {
        name: this.$t("dispatch.no"),
        styleHead: "w-12 text-gray-lp-300 text-sm height-header-table"
      },
      {
        name: this.$t("dispatch.noStt"),
        styleHead: "w-24 text-gray-lp-300 text-sm text-left",
      },
      {
        name: this.$t("dispatch.noReferensi"),
        styleHead: "w-32 text-gray-lp-300 text-sm text-left",
      },
      {
        name: this.$t("dispatch.totalBerat"),
        styleHead: "w-16 text-gray-lp-300 text-sm text-left",
      },
      {
        name: this.$t("dispatch.ursaCode"),
        styleHead: "w-16 text-gray-lp-300 text-sm text-left",
      },
      {
        name: this.$t("dispatch.noDem"),
        styleHead: "w-24 text-gray-lp-300 text-sm text-left",
      },
    ];
  }

  // get detail
  async created() {
    await this.getDetail();
  }

  get noDetailDispatch() {
    return this.$route.query?.dispatchNo || "";
  }

  get reportListData() {
    const sttListSuccess = this.detailManifest.stts.filter(e =>
      new RegExp("success", "i").test(e.ddScanStatus)
    );

    if (this.searchValue) {
      const filterData = sttListSuccess.filter(
        (manifest) =>
          manifest.ddSttNo
            .toLowerCase()
            .includes(this.searchValue.toLowerCase()) ||
          manifest.ddDemNo
            .toLowerCase()
            .includes(this.searchValue.toLowerCase())
      );

      return filterData;
    }

    return sttListSuccess;
  }

  limitLengthName(name: string) {
    return limitLengthName(name, 30);
  }

  formatDateFunc(date: string, format?: string) {
    return formatDateBasedTimezoneValue(date, format);
  }

  goBack() {
    this.$router.push("/dispatch");
  }

  _onGetSearch(value: string) {
    if (!value || value.length > 0) {
      this.searchValue = value;
    }
  }

  onSearch(value: string) {
    this._onGetSearch(value);
  }

  clearSearch() {
    this.searchValue = "";
    this._onGetSearch("");
  }

  async _onPrintDispatchManifest() {
    MainAppController.showLoading();
    await onPrintDispatchManifest(this.detailManifest);
    MainAppController.closeLoading();
  }

  async _onDownloadData() {
    const id = this.$route.params?.id;
    const fileName = `dispatch_${id}_${moment().format("YYYY-MM-DD")}`;

    MainAppController.showLoading();
    await downloadFile(
      `${Endpoints.baseUrl}/hydra/v1/dispatch/${id}/xlsx`,
      fileName,
      ".xlsx",
      true
    );
    MainAppController.closeLoading();
  }

  async getDetail() {
    try {
      this.errorData = new ErrorMessageEntities();
      this.isLoading = true;
      this.isError = false;
      const id = Number(this.$route.params.id);
      this.detailManifest = await DispatchController.getDetail(id);
    } catch (error) {
      this.isError = true;
      this.errorData = parsingErrorResponse(error);

      if (
        this.errorData.type === INTERNAL_SERVER_ERROR ||
        this.errorData.type === CLIENT_ERROR
      ) {
        this.errorData.image = "error-token-ngen";
        this.errorData.title = this.$t("dispatch.errorSystem");
        this.errorData.message = this.$t("dispatch.errorSystemMessage");
      } else if (this.errorData.type === INTERNET_ERROR) {
        this.errorData.title = this.$t("dispatch.errorInternet");
        this.errorData.message = this.$t("dispatch.errorInternetMessage");
      }
    } finally {
      this.isLoading = false;
    }
  }

  get isEmpty() {
    return !this.reportListData.length && !this.isLoading && !this.searchValue;
  }

  formatGrossWeight(grossWeight: number) {
    return grossWeight ? `${Number(grossWeight)
      .toString()
      .replace(".", ",")} kg` : '-';
  }
}
